import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { ClerkProvider } from '@clerk/clerk-react';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';

const clerkPubKey = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY;

if (!clerkPubKey) {
  console.warn('Missing Clerk publishable key. Auth will not work.');
}

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

root.render(
  <React.StrictMode>
    <ClerkProvider
      publishableKey={clerkPubKey || ''}
      localization={{
        socialButtonsBlockButton: 'Sign in with {{provider}}',
        signIn: {
          start: {
            title: 'Sign in to astra',
            titleCombined: 'Sign in to astra'
          },
          emailCode: {
            title: 'Check your email',
            subtitle: 'Enter the verification code to continue to Astra'
          }
        }
      }}
      appearance={{
        variables: {
          colorPrimary: '#818cf8',
          colorBackground: 'rgba(15, 23, 42, 0.45)',
          colorInputBackground: 'rgba(255, 255, 255, 0.1)',
          colorInputText: '#e2e8f0',
          colorText: '#e2e8f0',
          colorTextSecondary: '#94a3b8',
          borderRadius: '16px'
        },
        elements: {
          card: {
            background:
              'linear-gradient(145deg, rgba(255,255,255,0.2), rgba(148,163,184,0.08) 40%, rgba(15,23,42,0.28))',
            border: '1px solid rgba(255,255,255,0.32)',
            boxShadow: '0 24px 60px rgba(2,6,23,0.42), inset 0 1px 0 rgba(255,255,255,0.25)',
            backdropFilter: 'blur(24px) saturate(160%)'
          },
          modalBackdrop: {
            backdropFilter: 'blur(10px)',
            backgroundColor: 'rgba(2, 6, 23, 0.32)'
          },
          formButtonPrimary: {
            background:
              'linear-gradient(135deg, rgba(129,140,248,0.95), rgba(99,102,241,0.9))',
            border: '1px solid rgba(255,255,255,0.22)',
            boxShadow: '0 10px 24px rgba(79,70,229,0.35)'
          },
          formFieldInput: {
            backgroundColor: 'rgba(255,255,255,0.08)',
            border: '1px solid rgba(255,255,255,0.22)',
            color: '#f8fafc'
          },
          formFieldInput__focus: {
            backgroundColor: 'rgba(255,255,255,0.14)',
            border: '1px solid rgba(165,180,252,0.82)',
            boxShadow: '0 0 0 2px rgba(129,140,248,0.32), inset 0 1px 0 rgba(255,255,255,0.18)'
          },
          otpCodeFieldInput: {
            backgroundColor: 'rgba(255,255,255,0.14)',
            border: '1px solid rgba(255,255,255,0.34)',
            color: '#ffffff',
            boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.16), 0 8px 18px rgba(2,6,23,0.25)',
            backdropFilter: 'blur(14px) saturate(145%)',
            fontWeight: 700
          },
          otpCodeFieldInput__focus: {
            backgroundColor: 'rgba(255,255,255,0.2)',
            border: '1px solid rgba(165,180,252,0.8)',
            boxShadow: '0 0 0 2px rgba(129,140,248,0.35), inset 0 1px 0 rgba(255,255,255,0.2)'
          },
          socialButtonsBlockButton: {
            backgroundColor: 'rgba(255,255,255,0.08)',
            border: '1px solid rgba(255,255,255,0.22)',
            color: '#f8fafc',
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.18)',
              border: '1px solid rgba(255,255,255,0.4)',
              color: '#ffffff'
            }
          },
          socialButtonsBlockButtonText: {
            color: '#f8fafc',
            fontWeight: 600
          },
          userButtonPopoverCard: {
            background:
              'linear-gradient(145deg, rgba(15,23,42,0.9), rgba(30,41,59,0.84))',
            border: '1px solid rgba(148,163,184,0.3)',
            boxShadow: '0 20px 44px rgba(2,6,23,0.5)',
            backdropFilter: 'blur(18px) saturate(150%)'
          },
          userButtonPopoverMain: {
            backgroundColor: 'transparent'
          },
          userButtonPopoverActions: {
            borderTop: '1px solid rgba(148,163,184,0.24)'
          },
          userButtonPopoverActionButton: {
            color: '#e2e8f0',
            borderRadius: '10px',
            '&:hover': {
              backgroundColor: 'rgba(129,140,248,0.16)',
              color: '#ffffff'
            }
          },
          userButtonPopoverActionButtonIcon: {
            color: '#a5b4fc'
          },
          userButtonPopoverFooter: {
            background: 'rgba(15,23,42,0.72)',
            borderTop: '1px solid rgba(148,163,184,0.22)'
          },
          userButtonPopoverFooterPagesLink: {
            color: '#a5b4fc'
          },
          userPreviewMainIdentifier: {
            color: '#f8fafc',
            fontWeight: 600
          },
          userPreviewSecondaryIdentifier: {
            color: '#cbd5e1'
          },
          cardBox: {
            background: 'rgba(2, 6, 23, 0.3)',
            border: '1px solid rgba(148,163,184,0.24)',
            boxShadow: '0 28px 70px rgba(2,6,23,0.55)',
            backdropFilter: 'blur(20px) saturate(150%)'
          },
          pageScrollBox: {
            background:
              'linear-gradient(145deg, rgba(15,23,42,0.86), rgba(15,23,42,0.74))'
          },
          navbar: {
            background:
              'linear-gradient(180deg, rgba(15,23,42,0.94), rgba(15,23,42,0.82))',
            borderRight: '1px solid rgba(148,163,184,0.26)'
          },
          navbarButton: {
            color: '#cbd5e1',
            borderRadius: '10px',
            '&:hover': {
              backgroundColor: 'rgba(129,140,248,0.16)',
              color: '#ffffff'
            }
          },
          navbarButton__active: {
            backgroundColor: 'rgba(129,140,248,0.26)',
            color: '#ffffff'
          },
          navbarButtonIcon: {
            color: '#a5b4fc'
          },
          navbarButtonText: {
            color: '#e2e8f0',
            fontWeight: 600
          },
          profilePage: {
            backgroundColor: 'rgba(2,6,23,0.18)'
          },
          profileSection: {
            borderTop: '1px solid rgba(148,163,184,0.2)'
          },
          profileSectionTitleText: {
            color: '#f8fafc',
            fontWeight: 700
          },
          profileSectionSubtitleText: {
            color: '#cbd5e1'
          },
          profileSectionContent: {
            backgroundColor: 'rgba(15,23,42,0.42)',
            border: '1px solid rgba(148,163,184,0.2)',
            borderRadius: '12px'
          },
          profileSectionPrimaryButton: {
            background: 'transparent',
            border: 'none',
            boxShadow: 'none',
            color: '#818cf8',
            padding: '0',
            minHeight: '0',
            justifyContent: 'flex-start',
            fontWeight: 500,
            '&:hover': {
              background: 'transparent',
              color: '#a5b4fc'
            },
            '&:active': {
              background: 'transparent',
              color: '#a5b4fc'
            }
          },
          menuButton: {
            color: '#e2e8f0',
            '&:hover': {
              backgroundColor: 'rgba(129,140,248,0.16)',
              color: '#ffffff'
            }
          }
        }
      }}
    >
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </ClerkProvider>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
